// Centralized API client utilities
import { authFetch } from '../lib/authFetch';
import { extractErrorMessage } from '../lib/apiErrorHandling';
import { readEnvString } from '../lib/env';
import { sanitizeApiBase, warnIfUnsafeApiBase } from '../lib/apiBase';
import type { ZodType } from 'zod';

// Backward-compatible response shape used by some feature helpers.
export type JsonResponse<T = unknown> = { ok: boolean; status: number; data: T | null };

function appendRequestId(message: string, requestId: string | null): string {
  const trimmed = message?.trim() || 'Request failed';
  const id = requestId?.trim() || '';
  if (!id) {
    return trimmed;
  }
  if (trimmed.toLowerCase().includes('request id')) {
    return trimmed;
  }
  return `${trimmed} (request id: ${id})`;
}

export function resolveBase(override?: string | null, context = 'api.resolveBase'): string {
  const envBase = readEnvString('VITE_API_BASE');
  const sanitized = sanitizeApiBase(override ?? envBase);
  if (sanitized) {
    warnIfUnsafeApiBase(sanitized, context);
  }
  return sanitized;
}

// Standard JSON helper for endpoints that return { status: 'success', ... }
// Simplified to follow KISS principle - removes complex status field checking
export async function json<T = unknown>(res: Response): Promise<T> {
  if (!res || typeof res.ok !== 'boolean') {
    throw new Error('Request failed (invalid response)');
  }

  const requestId = res.headers.get('X-Request-Id');
  const { data, raw, parseError } = await parseJsonWithRaw<T>(res);
  
  if (parseError) {
    if (!res.ok) {
      throw new Error(appendRequestId(`Request failed (${res.status})`, requestId));
    }
    throw new Error(appendRequestId(`Failed to parse response: ${parseError.message}`, requestId));
  }
  
  if (!res.ok) {
    const message = extractErrorMessage(data, raw, `Request failed (${res.status})`);
    throw new Error(appendRequestId(message, requestId));
  }
  
  // Simple check: if response has status='error', throw
  // Note: We don't check for task/deal/planning statuses here - those are domain-specific
  // and should be handled by the calling code if needed
  if (data && typeof data === 'object' && 'status' in data && (data as { status?: unknown }).status === 'error') {
    const message = extractErrorMessage(data, raw, 'Request failed');
    throw new Error(appendRequestId(message, requestId));
  }

  return data as T;
}

export async function jsonWithSchema<T>(res: Response, schema: ZodType<T>): Promise<T> {
  const payload = await json<unknown>(res);
  const parsed = schema.safeParse(payload);
  if (parsed.success) {
    return parsed.data;
  }

  const requestId = res.headers.get('X-Request-Id');
  const issues = parsed.error.issues
    .slice(0, 3)
    .map((issue) => {
      const path = issue.path?.length ? issue.path.join('.') : '(root)';
      return `${path}: ${issue.message}`;
    })
    .join('; ');
  const message = issues ? `Invalid response shape: ${issues}` : 'Invalid response shape';
  throw new Error(appendRequestId(message, requestId));
}

export type ParsedJsonWithRaw<T> = {
  data: T | null;
  raw: string;
  parseError?: Error;
};

export async function parseJsonWithRaw<T = unknown>(res: Response): Promise<ParsedJsonWithRaw<T>> {
  let raw = '';

  try {
    raw = await res.text();
  } catch (error) {
    const parseError =
      error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Failed to read response body');
    return { data: null, raw: '', parseError };
  }

  if (!raw) {
    return { data: null, raw: '' };
  }

  try {
    const data = JSON.parse(raw) as T;
    return { data, raw };
  } catch (error) {
    const parseError =
      error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Invalid JSON response');
    return { data: null, raw, parseError };
  }
}

export async function requestJson<T = unknown>(
  url: string,
  init?: (RequestInit & { timeoutMs?: number }),
): Promise<JsonResponse<T>> {
  const controller = new AbortController();
  const { timeoutMs = 10000, signal, ...restInit } = init ?? {};
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await authFetch(url, {
      ...restInit,
      signal: signal ?? controller.signal,
    });
    const parsed = await parseJsonWithRaw<T>(res);
    return { ok: res.ok, status: res.status, data: parsed.data ?? null };
  } finally {
    clearTimeout(timeout);
  }
}

export { authFetch };
